import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronUp, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CurrencyIcon } from '@/components/profile/CurrencyIcon'
import { formatBalance } from '@/lib/format-number'
import { cn } from '@/lib/utils'
import { useGetCurrenciesQuery } from '@/store/api/paymentApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import { selectSlideGame, setSlideBetAmount, setSlideBetMode, setSlideTargetMultiplier } from '@/store/slices/slideSlice'
import type { SlideBetMode } from '@/types/slide'
import type { GameController } from '@/components/original/games/slide/core/GameController'

interface SlideBetPanelProps {
	controller: GameController | null
	className?: string
}

function formatAmount(value: number) {
	return value.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

export function SlideBetPanel({ controller, className }: SlideBetPanelProps) {
	const dispatch = useAppDispatch()
	const slide = useAppSelector(selectSlideGame)
	const isAuthenticated = useAppSelector(selectIsAuthenticated)
	const { data: currenciesResponse } = useGetCurrenciesQuery()
	const [amountDraft, setAmountDraft] = useState(slide.betAmount)
	const [targetDraft, setTargetDraft] = useState(slide.targetMultiplier)
	const [playersOpen, setPlayersOpen] = useState(false)

	const currencies = currenciesResponse?.data ?? []

	const currency = useMemo(() => currencies.find((item) => item.currency_code.toUpperCase() === slide.currencyCode.toUpperCase()), [currencies, slide.currencyCode])

	const usdAmount = useMemo(() => {
		const amount = Number(amountDraft)
		const rate = Number(currency?.exchange_rate ?? 0)

		if (!Number.isFinite(amount) || !Number.isFinite(rate)) {
			return 0
		}

		return amount * rate
	}, [amountDraft, currency?.exchange_rate])

	const potentialPayout = useMemo(() => {
		const amount = Number(amountDraft)
		const target = Number(targetDraft)

		if (!Number.isFinite(amount) || !Number.isFinite(target) || amount <= 0 || target < 1) {
			return 0
		}

		return amount * target
	}, [amountDraft, targetDraft])

	useEffect(() => {
		setAmountDraft(slide.betAmount)
	}, [slide.betAmount])

	useEffect(() => {
		setTargetDraft(slide.targetMultiplier)
	}, [slide.targetMultiplier])

	// Freeze inputs while a bet is live or queued for the next round.
	const amountLocked = slide.scheduledBet || slide.hasBet

	const action = useMemo(() => {
		if (!isAuthenticated) {
			return {
				label: 'Bet',
				variant: 'secondary' as const,
				disabled: true,
				onClick: () => undefined,
			}
		}

		if (slide.phase === 'waiting') {
			if (slide.hasBet) {
				return { label: 'Cancel', variant: 'destructive' as const, disabled: slide.isSubmitting, onClick: () => void controller?.cancelBet() }
			}

			return {
				label: slide.autoBetEnabled ? 'Auto Bet' : 'Bet',
				variant: 'default' as const,
				disabled: slide.isSubmitting,
				onClick: () => void controller?.placeBet(),
			}
		}

		// playing / result: live bet stays locked until the strip finishes landing; otherwise queue next round.
		if (slide.phase === 'playing' || slide.phase === 'result') {
			if (slide.hasBet) {
				return {
					label: `Target ${Number(slide.targetMultiplier).toFixed(2)}x`,
					variant: 'secondary' as const,
					disabled: true,
					onClick: () => undefined,
				}
			}

			if (slide.scheduledBet) {
				return { label: 'Cancel Next', variant: 'destructive' as const, disabled: false, onClick: () => void controller?.cancelBet() }
			}

			return {
				label: slide.autoBetEnabled ? 'Auto Bet Next' : 'Bet Next',
				variant: 'secondary' as const,
				disabled: false,
				onClick: () => void controller?.placeBet(),
			}
		}

		if (slide.scheduledBet) {
			return { label: 'Cancel Next', variant: 'destructive' as const, disabled: false, onClick: () => void controller?.cancelBet() }
		}

		return {
			label: slide.autoBetEnabled ? 'Auto Bet' : 'Bet',
			variant: 'secondary' as const,
			disabled: true,
			onClick: () => undefined,
		}
	}, [controller, isAuthenticated, slide.autoBetEnabled, slide.hasBet, slide.isSubmitting, slide.phase, slide.scheduledBet, slide.targetMultiplier])

	const handleModeChange = useCallback(
		(value: string) => {
			dispatch(setSlideBetMode(value as SlideBetMode))
		},
		[dispatch],
	)

	const commitAmount = useCallback(() => {
		const parsed = Number(amountDraft)
		if (!Number.isFinite(parsed) || parsed <= 0) {
			setAmountDraft(slide.betAmount)
			return
		}

		dispatch(setSlideBetAmount(parsed.toString()))
	}, [amountDraft, dispatch, slide.betAmount])

	const commitTarget = useCallback(() => {
		let parsed = Number(targetDraft)
		if (!Number.isFinite(parsed) || parsed < 1) {
			parsed = 1
		}

		const next = parsed.toFixed(2)
		setTargetDraft(next)
		dispatch(setSlideTargetMultiplier(next))
	}, [dispatch, targetDraft])

	const handleQuickAmount = useCallback(
		(multiplier: number) => {
			const parsed = Number(amountDraft) || Number(slide.betAmount) || 0
			const next = Math.max(0, parsed * multiplier)
			const nextAmount = next > 0 ? String(next) : '0'

			setAmountDraft(nextAmount)
			dispatch(setSlideBetAmount(nextAmount))
		},
		[amountDraft, dispatch, slide.betAmount],
	)

	return (
		<div className={cn('flex h-full min-h-0 w-full flex-col gap-4 overflow-hidden rounded-2xl border border-border bg-card p-4', className)}>
			<Tabs value={slide.betMode} onValueChange={handleModeChange} className='shrink-0'>
				<TabsList className='grid h-11 w-full grid-cols-2 rounded-xl bg-secondary p-1'>
					<TabsTrigger value='manual' className='rounded-lg data-[state=active]:bg-background'>
						Manual
					</TabsTrigger>
					<TabsTrigger value='auto' className='rounded-lg data-[state=active]:bg-background'>
						Auto
					</TabsTrigger>
				</TabsList>
			</Tabs>

			<div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto'>
			<div className={cn('space-y-2', amountLocked && 'pointer-events-none opacity-50')}>
				<div className='flex items-center justify-between gap-3'>
					<label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>Bet Amount</label>
					<span className='text-xs font-medium text-muted-foreground'>${formatBalance(usdAmount, 2)}</span>
				</div>
				<div className='flex items-stretch overflow-hidden rounded-xl border border-border bg-secondary'>
					<div className='relative min-w-0 flex-1'>
						<input
							type='text'
							inputMode='decimal'
							value={amountDraft}
							onChange={(event) => setAmountDraft(event.target.value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1'))}
							onBlur={commitAmount}
							className='flex h-11 w-full bg-transparent pl-3 text-sm font-semibold tracking-[-0.21px] text-foreground focus:outline-none sm:text-base sm:tracking-[-0.24px]'
						/>
						<CurrencyIcon
							code={slide.currencyCode}
							symbolUrl={currency?.symbol_url}
							size='sm'
							className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2'
						/>
					</div>
					<div className='flex w-[88px] shrink-0 border-l border-border'>
						<button type='button' onClick={() => handleQuickAmount(0.5)} className='flex-1 text-sm font-medium text-foreground transition-colors hover:bg-accent'>
							½
						</button>
						<span className='w-px bg-border' aria-hidden />
						<button type='button' onClick={() => handleQuickAmount(2)} className='flex-1 text-sm font-medium text-foreground transition-colors hover:bg-accent'>
							2×
						</button>
					</div>
				</div>
			</div>

			{/* Target is required on Slide (unlike Crash auto-cashout which is optional). */}
			<div className={cn('space-y-2', amountLocked && 'pointer-events-none opacity-50')}>
				<div className='flex items-center justify-between gap-3'>
					<label htmlFor='slide-target-multiplier' className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
						Target Multiplier
					</label>
					<span className='text-xs font-medium text-muted-foreground'>Pays {formatAmount(potentialPayout)}</span>
				</div>
				<input
					id='slide-target-multiplier'
					type='text'
					inputMode='decimal'
					value={targetDraft}
					onChange={(event) => setTargetDraft(event.target.value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1'))}
					onBlur={commitTarget}
					className='flex h-11 w-full rounded-xl border border-border bg-transparent pl-3 text-sm font-semibold tracking-[-0.21px] text-foreground focus:outline-none sm:text-base sm:tracking-[-0.24px]'
				/>
			</div>

			<Collapsible
				open={playersOpen}
				onOpenChange={setPlayersOpen}
				className={cn(
					'flex min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-secondary/60',
					playersOpen ? 'max-h-[240px] flex-1 xl:max-h-none' : 'shrink-0',
				)}
			>
				<CollapsibleTrigger className='flex h-11 w-full shrink-0 items-center gap-2.5 px-3 text-sm transition-colors hover:bg-background/30'>
					<Users className='h-6 w-6 shrink-0 text-muted-foreground' aria-hidden />
					<span className='font-medium tabular-nums text-foreground'>{slide.playerCount}</span>
					<span className='ml-auto flex min-w-0 items-center gap-1.5'>
						<CurrencyIcon code={slide.currencyCode} symbolUrl={currency?.symbol_url} size='sm' className='shrink-0' />
						<span className='truncate font-medium tabular-nums text-foreground'>{formatBalance(slide.totalBetAmount)}</span>
					</span>
					<ChevronUp className={cn('h-6 w-6 shrink-0 text-muted-foreground transition-transform duration-200', !playersOpen && 'rotate-180')} aria-hidden />
				</CollapsibleTrigger>

				<CollapsibleContent className='min-h-0 flex-1 overflow-hidden data-[state=closed]:hidden'>
					<div className='h-full max-h-[200px] overflow-y-auto border-t border-border xl:max-h-none'>
						{slide.players.length === 0 ? (
							<p className='px-3 py-5 text-center text-sm text-muted-foreground'>Waiting for players…</p>
						) : (
							<ul>
								{slide.players.map((player) => {
									const playerCurrencyCode = player.currencyCode ?? slide.currencyCode
									const playerCurrency = currencies.find((item) => item.currency_code.toUpperCase() === playerCurrencyCode.toUpperCase())
									const displayName = player.username?.trim() || 'Hidden'

									return (
										<li
											key={player.playerId}
											className='grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 px-3 py-1.5 text-sm hover:bg-background/40'
										>
											<span className={cn('truncate', displayName === 'Hidden' ? 'text-muted-foreground' : 'font-medium text-foreground')}>
												{displayName}
											</span>
											<span className={cn('min-w-[3.25rem] text-right tabular-nums', player.isWinner ? 'font-semibold text-success' : 'text-foreground')}>
												{player.targetMultiplier != null ? `${Number(player.targetMultiplier).toFixed(2)}x` : '—'}
											</span>
											<span className='flex min-w-[4.5rem] items-center justify-end gap-1.5 tabular-nums text-foreground'>
												<CurrencyIcon code={playerCurrencyCode} symbolUrl={playerCurrency?.symbol_url} size='sm' />
												<span>{formatAmount(player.betAmount)}</span>
											</span>
										</li>
									)
								})}
							</ul>
						)}
					</div>
				</CollapsibleContent>
			</Collapsible>
			</div>

			<div className='shrink-0'>
			<Button type='button' variant={action.variant} disabled={action.disabled || !controller} onClick={action.onClick} className='h-12 w-full text-base font-semibold'>
				{action.label}
			</Button>
			</div>
		</div>
	)
}
